import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { MultiStepper, H4 } from '@tviklax/ui-components';
import styled from 'styled-components';
import { FiUserCheck } from "react-icons/fi";
import axios from "axios";
import {
    BUSINESS_TYPES_LIST,
    AGENTS_LIST,
    STATES_LIST,
} from "../../config/api";
import { useCountry } from "../../utils/countryUtils";
import {
    SellerInformationForm,
    PartnerInformationForm,
    CompanyInformationForm,
    AgentInformationForm,
    CompleteRegistrationForm
} from "../../features/registration/seller-registration/components";
import { SellerRegistrationFormData } from "../../features/registration/components";
import { showToast } from "../../utils/toastUtils";
import FullPageLoader from "../../components/common/FullPageLoader";

const RegistHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 1.875rem;
`;

const RegistIcon = styled.div`
  width: 3.75rem;
  height: 3.75rem;
  background: #fff5f0;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const IconWrapper = styled.div`
  color: #f86400;
  font-size: 1.875rem;
  display: flex;
`;

const RegistTitle = styled.div`
  h4 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #000;
  }
  p {
    margin: 0.3125rem 0 0;
    color: #64748b;
    font-size: 0.875rem;
  }
`;

export const SellerRegistrationPage: React.FC = () => {
    const {
        country,
        sellerAvailableCountries,
    } = useCountry();

    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState<SellerRegistrationFormData>({
        sellerType: 1,
        isRetail: true,
        isWholesale: false,
        firstname: "",
        lastname: "",
        email: "",
        password: "",
        confirmPassword: "",
        mobileNumber: "",
        emailVerified: false,
        mobileVerified: false,
        businesstype: "",
        companyname: "",
        companyaddress: "",
        address_line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "",
        latitude: "",
        longitude: "",
        gstin: "",
        gstinVerified: false,
        iec_code: "",
        iecVerified: false,
        iecResponse: null,
        gstinResponse: null,
        referredByAgent: false,
        agentId: "",
        terms: false,
        ein_number: "",
        ssn_number: "",
        supplierKycDocumentUrl: "",
        supplierKycDocumentName: "",
    });

    const [businessTypesOptions, setBusinessTypesOptions] = useState([]);
    const [businesstypeLoading, setBusinesstypeLoading] = useState(false);
    const [agentsOptions, setAgentsOptions] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [stateOptions, setStateOptions] = useState([]);
    const [fullPageLoading] = useState(false);
    const defaultStepLabels = {
        seller: 'Next',
        partner: 'Next',
        company: 'Next',
        agent: 'Next',
        complete: 'Submit',
    };
    const [stepNavDisabled, setStepNavDisabled] = useState({
        seller: false,
        partner: false,
        company: false,
        agent: false,
        complete: false,
    });
    const [stepNextLabel, setStepNextLabel] = useState(defaultStepLabels);

    useEffect(() => {
        setFormData((prev: SellerRegistrationFormData) => ({ ...prev, country: country }));
    }, [country]);

    useEffect(() => {
        if (country) {
            setBusinesstypeLoading(true);
            axios
                .get(BUSINESS_TYPES_LIST, { params: { country: country } })
                .then((response) => {
                    if (response?.data?.success && response?.data?.code === 200) {
                        setBusinessTypesOptions(response?.data?.data);
                    }
                })
                .catch((err) => console.error(err))
                .finally(() => setBusinesstypeLoading(false));

            setAgentsLoading(true);
            axios
                .get(AGENTS_LIST, { params: { country: country } })
                .then((response) => {
                    if (response?.data?.success && response?.data?.code === 200) {
                        setAgentsOptions(response?.data?.data);
                    }
                })
                .catch((err) => console.error(err))
                .finally(() => setAgentsLoading(false));

            axios
                .get(STATES_LIST, { params: { country: country } })
                .then((response) => {
                    if (response?.data?.success && response?.data?.code === 200) {
                        setStateOptions(response?.data?.data);
                    }
                })
                .catch((err) => console.error(err));
        }
    }, [country]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        window.scrollTo(0, 0);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
        window.scrollTo(0, 0);
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData((prev: SellerRegistrationFormData) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFormSubmit = () => {
        showToast("Registration Successful!", "success");
        // Redirect or show success message
    };

    const updateStepDisabled = (step: keyof typeof stepNavDisabled, disabled: boolean) => {
        setStepNavDisabled((prev) => ({ ...prev, [step]: disabled }));
    };

    const updateStepLabel = (step: keyof typeof stepNextLabel, label?: string) => {
        setStepNextLabel((prev) => ({
            ...prev,
            [step]: label ?? defaultStepLabels[step],
        }));
    };

    const steps = [
        {
            label: "Seller Information",
            formId: "seller-information-form",
            nextDisabled: stepNavDisabled.seller,
            nextLabel: stepNextLabel.seller,
            content: (
                <SellerInformationForm
                    formId="seller-information-form"
                    formData={formData}
                    setFormData={setFormData}
                    handleChange={handleChange}
                    handleNext={handleNext}
                    setNextDisabled={(disabled) => updateStepDisabled('seller', disabled)}
                />
            ),
        },
        {
            label: "Personal Information",
            formId: "partner-information-form",
            nextDisabled: stepNavDisabled.partner,
            nextLabel: stepNextLabel.partner,
            content: (
                <PartnerInformationForm
                    formId="partner-information-form"
                    formData={formData}
                    setFormData={setFormData}
                    handleChange={handleChange}
                    handleNext={handleNext}
                    availableCountries={sellerAvailableCountries}
                    country={country}
                    setNextDisabled={(disabled) => updateStepDisabled('partner', disabled)}
                    setNextLabel={(label) => updateStepLabel('partner', label)}
                />
            ),
        },
        {
            label: "Company Information",
            formId: "company-information-form",
            nextDisabled: stepNavDisabled.company,
            nextLabel: stepNextLabel.company,
            content: (
                <CompanyInformationForm
                    formId="company-information-form"
                    formData={formData}
                    setFormData={setFormData}
                    handleChange={handleChange}
                    handleNext={handleNext}
                    businesstypeLoading={businesstypeLoading}
                    businessTypesOptions={businessTypesOptions}
                    country={country}
                    stateOptions={stateOptions}
                    setNextDisabled={(disabled) => updateStepDisabled('company', disabled)}
                    setNextLabel={(label) => updateStepLabel('company', label)}
                />
            ),
        },
        {
            label: "Agent Information",
            formId: "agent-information-form",
            nextDisabled: stepNavDisabled.agent,
            nextLabel: stepNextLabel.agent,
            content: (
                <AgentInformationForm
                    formId="agent-information-form"
                    formData={formData}
                    handleChange={handleChange}
                    handleNext={handleNext}
                    loading={false}
                    agentsLoading={agentsLoading}
                    agentsOptions={agentsOptions}
                    setNextDisabled={(disabled) => updateStepDisabled('agent', disabled)}
                />
            ),
        },
        {
            label: "Complete Registration",
            formId: "complete-registration-form",
            nextDisabled: stepNavDisabled.complete,
            nextLabel: stepNextLabel.complete,
            content: (
                <CompleteRegistrationForm
                    formId="complete-registration-form"
                    formData={formData}
                    handleFormSubmit={handleFormSubmit}
                    setNextDisabled={(disabled) => updateStepDisabled('complete', disabled)}
                    setNextLabel={(label) => updateStepLabel('complete', label)}
                />
            ),
        },
    ];

    return (
        <Box sx={{ maxWidth: '1200px', margin: '0 auto', px: 2 }}>
            <FullPageLoader loading={fullPageLoading} color="#f86400" />
            <Box sx={{ maxWidth: '900px', margin: '0 auto', py: 5 }}>
                <RegistHeader>
                    <RegistIcon>
                        <IconWrapper>
                            <FiUserCheck />
                        </IconWrapper>
                    </RegistIcon>
                    <RegistTitle>
                        <H4>Seller Registration</H4>
                        <p>Join us as a seller and grow your business.</p>
                    </RegistTitle>
                </RegistHeader>

                <MultiStepper
                    steps={steps}
                    activeStep={activeStep}
                    onStepChange={setActiveStep}
                    onNext={handleNext}
                    onBack={handleBack}
                    variant="progress"
                    completionContent={
                        <Box>
                            <H4 style={{ marginBottom: '1rem' }}>
                                Registration Completed!
                            </H4>
                            <H4>
                                Thank you for registering with Tvikla. We will review your application and get back to you shortly.
                            </H4>
                        </Box>
                    }
                />
            </Box>
        </Box>
    );
};
